import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { PaymentService } from '../../services/payment.service';
import { CompanyService } from '../../services/company.service';

import { AutoPayNotesListItem } from 'src/app/models/payment';
import { CompanyInformation } from '../../models/company';

export enum AutoPaymentActionType {
  Setup,
  Cancel,
  Ineligible
}

export interface AutoPaymentSelected {
  autoPaymentActionType: AutoPaymentActionType;
  selectedId: number;
  selectedData: AutoPayNotesListItem;
}

@Component({
  selector: 'app-payment-automatic-note-list',
  templateUrl: './payment-automatic-note-list.component.html',
  styleUrls: ['./payment-automatic-note-list.component.scss']
})
export class PaymentAutomaticNoteListComponent implements OnInit, AfterViewInit {
  private _participantId = 0;
  private _noteId = 0;

  @Input()
  set participantId(participantId: number) {
    this._participantId = participantId || 0;
    this.getData();
  }
  get participantId(): number {
    return this._participantId;
  }

  @Input() displayTitle = false;

  @Input() displayNotices = false;

  @Output() selected = new EventEmitter<AutoPaymentSelected>();

  // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
  // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
  private _hideElementDisplayStyle = 'none';
  private _showElementDisplayStyle = 'inline';
  private _showElementDisplayStyleIE = 'inline-block';

  private _handleError: HandleError;
  private _logging = environment.logging;
  private companyCode: string = null;                 // 10/22/2021

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;

  autoPaymentActionType = AutoPaymentActionType;
  autoPayNotesList: AutoPayNotesListItem[] = [];

  // DataTable
  tableDisplayedColumns = [
    'noteId',
    'noteStatus',
    'nextPaymentDueDate',
    'lastPaymentDate',
    'lastPaymentAmount',
    'totalAmountDue',
    'payoffAmount',
    'options'
  ];
  tableDataSource = new MatTableDataSource<AutoPayNotesListItem>(this.autoPayNotesList);
  tableDisplayStyle: string = this._hideElementDisplayStyle;
  tableDisplayStyleIE: string = this._hideElementDisplayStyle;
  autoPayNotesListFound = false;

  companyInformation: CompanyInformation;

  filteredNotesList = [...this.autoPayNotesList];
  selectedNoteStatus = '';
  uniqueNoteStatuses: string[] = [];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _paymentService: PaymentService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('PaymentAutomaticNoteListComponent');
  }

  ngOnInit() {
    this.autoPayNotesListFound = true;
    this.tableDisplayStyle = this.autoPayNotesListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.autoPayNotesListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
  }

  ngAfterViewInit() {}

  getData(): void {
    this.showSpinner = true;

    this.companyInformation = this._companyService.getNewCompanyInformation();
    this.companyCode =  this._companyService.getCompanyCode();

    this._companyService.companyInformation.subscribe(
      response => (this.companyInformation = response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this.getAutoPayNotesList(this._participantId);
  }

  getAutoPayNotesList(participantId: number): void {
    this.showSpinner = true;
    this._paymentService.getAutoPayNotesList(participantId).subscribe(
      resultArray => {
        this.autoPayNotesList = resultArray;
        this.filteredNotesList = [...this.autoPayNotesList];
        this.uniqueNoteStatuses = [...new Set(this.autoPayNotesList.map(note => note.noteStatus))];
        
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticNoteListComponent.getAutoPayNotesList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticNoteListComponent.getAutoPayNotesList :: result = ' + JSON.stringify(resultArray));
        }
        this.tableDataSource = new MatTableDataSource<AutoPayNotesListItem>(this.autoPayNotesList);
        this.autoPayNotesListFound = resultArray.length > 0;
        this.tableDisplayStyle = this.autoPayNotesListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.autoPayNotesListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
        this.getDataComplete();
      },
      error => this._handleError('getAutoPayNotesList', error)
    );
  }

  filterNotes() {

    // this.selectedNoteId = null;

    if (this.selectedNoteStatus) {
      this.filteredNotesList = this.autoPayNotesList.filter(
        note => note.noteStatus === this.selectedNoteStatus
      );
    } else {
      this.filteredNotesList = [...this.autoPayNotesList];
    }
  }

  getDataComplete(): void {
    this.showSpinner = false;
  }

  selectNote(noteId: number, autoPaymentActionType: AutoPaymentActionType) {
    let autoPaymentSelected: AutoPaymentSelected;
    const selectedNoteId: number = noteId !== null && noteId > 0 ? noteId : null;
    let selectedData: AutoPayNotesListItem[] = [];
    selectedData = this.autoPayNotesList.filter(x => x.noteId === selectedNoteId);
    autoPaymentSelected = {
      autoPaymentActionType: autoPaymentActionType,
      selectedId: selectedNoteId,
      selectedData: selectedData.length > 0 ? selectedData[0] : null
    };

    if (autoPaymentActionType === AutoPaymentActionType.Ineligible) {
      this._userService.setNoteId(noteId);
      this._router.navigate(['payineligible']);
    } else {
      this.selected.emit(autoPaymentSelected);
    }
  }

  getToolTip(element: string): string {
    let toolTip: string;
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        toolTip = 'What does My Loan Status Mean?';
        break;
      }
      default: {
        toolTip = 'No ToolTip message has been defined for ' + element;
        break;
      }
    }
    return toolTip;
  }

  openToolTip(element: string): void {
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        if (this._logging.navigationTraceLog) {
          console.log('PaymentAutomaticNoteListComponent.openToolTip');
        }
        window.open(this.companyInformation.loanStatusDescriptionsURL, '_blank');
        break;
      }
    }
  }

  // 10/22/2021 - Get Company Code
  CompanyCode_Check(): boolean {
    for (const companyLookup of environment.companyCode.legaltext ) {
      if (companyLookup.companyCode === this.companyCode ) {
        return true;
      }
    }
    return false;
  }
}
